'use client';

import Link from 'next/link';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { exists } from 'src/utils/exists/exists';
import { FC } from 'react';

const ProfileDrawerCRMLinks: FC = () => {
  const { entityDetails } = useProfile();
  const links = entityDetails?.crm_links;

  if (!entityDetails) return null;

  const singleOwnerName = `${links[0]?.owner?.firstname} ${links[0]?.owner?.lastname}`;
  const singleStatus = links[0]?.crm_status;
  const objectType = links[0]?.crm_object_type;
  const hasLink = exists(entityDetails?.crm_links[0]?.crm_link);

  return (
    <div className="w-full flex flex-col gap-2">
      {links.length === 1 && (
        <>
          {singleOwnerName.length > 0 && (
            <div
              className="flex justify-between"
              data-testid="profile-drawer-crm-owner"
            >
              <div className="text-sm text-secondary leading-tight">Owner</div>
              <div className="text-sm font-medium text-primary leading-none">
                {singleOwnerName}
              </div>
            </div>
          )}
          {singleStatus && (
            <div
              className="flex justify-between"
              data-testid="profile-drawer-crm-status"
            >
              <div className="text-sm text-secondary leading-tight">Status</div>
              <div className="text-sm font-medium text-primary leading-none">
                {singleStatus}
              </div>
            </div>
          )}
          {hasLink && (
            <div
              className="flex justify-between"
              data-testid="profile-drawer-crm-link-row"
            >
              <div className="text-sm text-secondary leading-tight">Link</div>
              <Link
                href={entityDetails.crm_links[0].crm_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-end gap-1 text-sm font-medium text-link leading-none"
                aria-label={`View ${objectType} in CRM (opens in new tab)`}
                data-testid="profile-drawer-crm-link"
              >
                <span className="underline truncate">View {objectType}</span>
                <Icon icon={faExternalLink} aria-hidden="true" />
              </Link>
            </div>
          )}
        </>
      )}
      {links.length > 1 && (
        <>
          {links.map((link, index) => {
            const object = link.crm_object_type;
            const owner = `${link.owner.firstname} ${link.owner.lastname}`;
            const status = link.crm_status;
            const hasCRMDetails = link.owner.length > 0 || exists(status);
            const defaultLink = 'View in CRM';

            return (
              <div key={index} className="flex justify-between pb-2">
                <div className="text-sm font-medium text-link leading-tight">
                  {object}
                </div>
                <Link
                  href={link.crm_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm font-medium text-link text-right"
                  aria-label={`View ${object} in CRM (opens in new tab)`}
                  data-testid={`profile-drawer-crm-link-${index}`}
                >
                  <span className="underline truncate max-w-[240px]">
                    {hasCRMDetails
                      ? [owner, status].filter(Boolean).join(' / ')
                      : defaultLink}
                  </span>
                  <Icon
                    className="pl-1"
                    icon={faExternalLink}
                    aria-hidden="true"
                  />
                </Link>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ProfileDrawerCRMLinks;
